/* ============================================================================
 * Modal de geração de Carta de Correção (CC-e)
 *
 * Consome:
 *   POST /fiscal/events/:id/cc-e   (CreateCceDto → GeneratedCceDto)
 *   GET  /fiscal/invoices?status=AUTHORIZED (picker de documento autorizado)
 *
 * Regras do contrato (CreateCceDto — fiscal-documents.dto.ts:307):
 *   - `motivo` obrigatório, mínimo 15 caracteres
 *   - `camposCorrigidos` opcional: [{ campo, valorAntigo?, valorNovo? }]
 *
 * A geração registra o evento localmente (sefazIntegrated: false) e devolve o
 * XML da CC-e (evento 110110) — não há transmissão SEFAZ real (decisão do
 * backend, documentada no controller). O resultado mostra o que a API devolve.
 * ========================================================================== */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../shared/auth/AuthContext';
import { formatDateTime } from '../../../shared/utils/format';
import { Button, LoadingSkeleton, Modal, useToast } from '@nexora/design-system';
import { useGenerateCce, useFiscalDocuments } from './fiscalDocumentsHooks';
import type { GeneratedCce, CorrectionField } from './fiscalDocumentsTypes';
import { shortAccessKey } from './fiscalStatusMaps';

export interface CceTarget {
  fiscalDocumentId: string;
  documentNumber: string | null;
  serie: string | null;
  type: string | null;
  accessKey: string | null;
}

interface GenerateCceModalProps {
  isOpen: boolean;
  /** Documento pré-selecionado (quando o usuário clica em "CC-e" numa linha). */
  initialTarget?: CceTarget | null;
  onClose: () => void;
}

/* Linha de campo corrigido — no máximo 3. */
interface CorrectionRow {
  campo: string;
  valorAntigo: string;
  valorNovo: string;
}

const EMPTY_ROW: CorrectionRow = { campo: '', valorAntigo: '', valorNovo: '' };

const STATUS_AUTHORIZED = 'AUTHORIZED';

export function GenerateCceModal({
  isOpen,
  initialTarget,
  onClose,
}: GenerateCceModalProps): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { addToast } = useToast();

  const generateCce = useGenerateCce(tenantId);

  const [target, setTarget] = useState<CceTarget | null>(initialTarget ?? null);
  const [docSearch, setDocSearch] = useState('');
  const [motivo, setMotivo] = useState('');
  const [rows, setRows] = useState<CorrectionRow[]>([EMPTY_ROW]);
  const [result, setResult] = useState<GeneratedCce | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  /* Picker de documentos autorizados (GET /fiscal/invoices?status=AUTHORIZED).
     Busca numérica: 44 dígitos → chave de acesso; senão → número do documento.
     Nunca manda os dois filtros juntos (o backend aplica AND). */
  const searchIsKey = /^\d{10,}$/.test(docSearch.trim());
  const { data: docsData, isLoading: docsLoading } = useFiscalDocuments(
    tenantId,
    {
      status: STATUS_AUTHORIZED,
      ...(docSearch.trim()
        ? searchIsKey
          ? { accessKey: docSearch.trim() }
          : { documentNumber: docSearch.trim() }
        : {}),
      limit: 20,
    },
  );

  /* Reinicia o estado a cada abertura. */
  useEffect(() => {
    if (!isOpen) return;
    setTarget(initialTarget ?? null);
    setDocSearch('');
    setMotivo('');
    setRows([EMPTY_ROW]);
    setResult(null);
    setErrorText(null);
  }, [isOpen, initialTarget]);

  const availableDocs = docsData?.data ?? [];
  const motivoMin = 15;
  const isValid = !!target && motivo.trim().length >= motivoMin;

  const fieldsForSubmit = (): CorrectionField[] =>
    rows
      .filter((r) => r.campo.trim().length > 0)
      .map((r) => ({
        campo: r.campo.trim(),
        valorAntigo: r.valorAntigo.trim() || undefined,
        valorNovo: r.valorNovo.trim() || undefined,
      }));

  const handleSubmit = async (): Promise<void> => {
    if (!target) return;
    setErrorText(null);
    try {
      const created = await generateCce.mutateAsync({
        id: target.fiscalDocumentId,
        input: {
          motivo: motivo.trim(),
          ...(fieldsForSubmit().length > 0 ? { camposCorrigidos: fieldsForSubmit() } : {}),
        },
      });
      setResult(created);
      addToast('CC-e gerada (registro local)', 'success');
    } catch (err) {
      setErrorText((err as Error).message ?? 'Erro ao gerar a CC-e');
    }
  };

  const handleClose = (): void => {
    if (generateCce.isPending) return;
    onClose();
  };

  const docLabel = (d: CceTarget): string => {
    const typeLabel = d.type === 'NFCE' ? 'NFC-e' : d.type === 'NFSE' ? 'NFS-e' : d.type ?? 'NF-e';
    const num = d.documentNumber ? `${d.documentNumber}${d.serie ? `/${d.serie}` : ''}` : '—';
    return `${typeLabel} ${num} · ${shortAccessKey(d.accessKey)}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={result ? 'CC-e gerada' : 'Gerar Carta de Correção (CC-e)'}
      size="lg"
      footer={
        result ? (
          <Button variant="outline" onClick={handleClose}>
            Fechar
          </Button>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={!isValid}
              loading={generateCce.isPending}
              onClick={() => void handleSubmit()}
            >
              Gerar CC-e
            </Button>
          </div>
        )
      }
    >
      {result ? (
        /* ── Resultado (GeneratedCceDto) ── */
        <div className="fiscal-form">
          <div
            role="alert"
            style={{ padding: '0.75rem 1rem', background: '#e8f0e8', border: '1px solid #b7d4b7', borderRadius: '6px', fontSize: '0.875rem', color: '#1b4d1b' }}
          >
            CC-e registrada para o documento. Ação registrada localmente — sem
            transmissão à SEFAZ (sefazIntegrated: false).
          </div>
          <div className="fiscal-detail-grid">
            <div className="fiscal-detail-item">
              <div className="label">Documento</div>
              <div className="value">{result.fiscalDocumentId}</div>
            </div>
            <div className="fiscal-detail-item">
              <div className="label">Sequência</div>
              <div className="value value--mono">{result.sequence}</div>
            </div>
            <div className="fiscal-detail-item span-2">
              <div className="label">Motivo</div>
              <div className="value">{result.motivo}</div>
            </div>
            <div className="fiscal-detail-item">
              <div className="label">Registrado em</div>
              <div className="value">{formatDateTime(result.registeredAt)}</div>
            </div>
            <div className="fiscal-detail-item">
              <div className="label">Integração SEFAZ</div>
              <div className="value">{result.sefazIntegrated ? 'Sim' : 'Não (registro local)'}</div>
            </div>
          </div>

          {result.correctionFields && result.correctionFields.length > 0 && (
            <div className="fiscal-form__row">
              <span className="fiscal-form__label">Campos corrigidos</span>
              {result.correctionFields.map((c, i) => (
                <div key={i} className="fiscal-detail-item">
                  <div className="value value--mono">
                    {c.campo}: {c.valorAntigo ?? '—'} → {c.valorNovo ?? '—'}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="fiscal-form__row">
            <span className="fiscal-form__label">XML do evento (110110)</span>
            <div className="fiscal-cloud">{result.eventXml}</div>
          </div>
        </div>
      ) : (
        /* ── Formulário ── */
        <div className="fiscal-form">
          {errorText && <div className="fiscal-form__error">{errorText}</div>}

          <div className="fiscal-form__row">
            <label className="fiscal-form__label">
              Documento autorizado <span style={{ color: '#ba1a1a' }}>*</span>
            </label>
            {initialTarget ? (
              <div style={{ padding: '0.5rem', background: '#f2f4f6', border: '1px solid #c4c6cf', borderRadius: '2px', fontSize: '0.875rem' }}>
                {docLabel(initialTarget)}
              </div>
            ) : (
              <>
                <input
                  value={docSearch}
                  placeholder="Buscar por número ou chave de acesso..."
                  onChange={(e) => setDocSearch(e.target.value)}
                  aria-label="Buscar documento"
                />
                <div style={{ border: '1px solid #c4c6cf', borderRadius: '4px', maxHeight: '160px', overflow: 'auto' }}>
                  {docsLoading && (
                    <div style={{ padding: '0.5rem' }}>
                      <LoadingSkeleton count={3} height="28px" />
                    </div>
                  )}
                  {!docsLoading && availableDocs.length === 0 && (
                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8125rem', color: '#43474e' }}>
                      Nenhum documento autorizado encontrado.
                    </div>
                  )}
                  {!docsLoading &&
                    availableDocs.map((d) => {
                      const mapped: CceTarget = {
                        fiscalDocumentId: d.id,
                        documentNumber: d.documentNumber,
                        serie: d.serie,
                        type: d.type,
                        accessKey: d.accessKey,
                      };
                      const selected = target?.fiscalDocumentId === d.id;
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setTarget(mapped)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '0.5rem 0.75rem',
                            border: 'none',
                            borderBottom: '1px solid #eceef0',
                            background: selected ? '#e8f0f8' : '#ffffff',
                            cursor: 'pointer',
                            fontSize: '0.8125rem',
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                          }}
                        >
                          {docLabel(mapped)}
                        </button>
                      );
                    })}
                </div>
                <span className="fiscal-form__hint">
                  {target
                    ? `Selecionado: ${docLabel(target)}`
                    : 'Selecione o documento autorizado que será corrigido.'}
                </span>
              </>
            )}
          </div>

          <div className="fiscal-form__row">
            <label className="fiscal-form__label">
              Motivo da correção <span style={{ color: '#ba1a1a' }}>*</span>
            </label>
            <textarea
              value={motivo}
              placeholder={`Descreva o motivo da correção (mínimo ${motivoMin} caracteres)...`}
              onChange={(e) => setMotivo(e.target.value)}
              aria-label="Motivo da correção"
            />
            <span className={`fiscal-form__hint${motivo && motivo.trim().length < motivoMin ? ' fiscal-form__error' : ''}`}>
              {motivo.trim().length}/${motivoMin} caracteres mínimos.
            </span>
          </div>

          <div className="fiscal-form__row">
            <span className="fiscal-form__label">Campos corrigidos (opcional)</span>
            {rows.map((row, idx) => (
              <div
                key={idx}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'center' }}
              >
                <input
                  value={row.campo}
                  placeholder="Campo (ex: vProd, xFant)"
                  onChange={(e) =>
                    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, campo: e.target.value } : r)))
                  }
                  aria-label={`Campo corrigido ${idx + 1}`}
                />
                <input
                  value={row.valorAntigo}
                  placeholder="Valor antigo"
                  onChange={(e) =>
                    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, valorAntigo: e.target.value } : r)))
                  }
                  aria-label={`Valor antigo ${idx + 1}`}
                />
                <input
                  value={row.valorNovo}
                  placeholder="Valor novo"
                  onChange={(e) =>
                    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, valorNovo: e.target.value } : r)))
                  }
                  aria-label={`Valor novo ${idx + 1}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remover linha ${idx + 1}`}
                  disabled={rows.length === 1}
                  onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))}
                >
                  ✕
                </Button>
              </div>
            ))}
            {rows.length < 3 && (
              <div>
                <Button variant="ghost" size="sm" onClick={() => setRows((rs) => [...rs, EMPTY_ROW])}>
                  + Adicionar campo
                </Button>
              </div>
            )}
            <span className="fiscal-form__hint">
              Se informados, viram o grupo infCorrecao da CC-e. Máx. 3 campos.
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
}